import captureException from 'lib/captureException';
import TrackingDataContext from 'contexts/TrackingDataContext';
import Layout from 'components/Layout';
import Dealer from 'components/Dealer';
import Error from 'pages/_error';
import StructuredDataComponent from 'components/StructuredData';
import { DEALER_PAGE_URL } from 'lib/ContentGroups';
import useAdSetup from 'lib/hooks/useAdSetup';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import { GetServerSidePropsContext } from 'next';
import { SourcePages } from 'lib/EventSchema';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { AgeType, PageType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { enableContentCaching } from 'lib/cacheControl';
import isIndexable from 'lib/isIndexable';
import { fetchDealerPage } from 'lib/apis/DealerPageApi';
import { DealerPage as DealerPageModel } from 'lib/models/DealerPage';

interface Props {
  dealerPage?: DealerPageModel;
  path?: string;
  errorStatusCode?: number;
  dealer?: Record<string, unknown>;
  reviews?: [];
  indexable: boolean;
}

function generateStructuredData({ url, dealer, reviews }: DealerPageModel): Record<string, any> {
  const { street, city, state, zip } = dealer.address;

  return {
    '@context': 'http://schema.org',
    '@type': 'http://schema.org/AutoDealer',
    id: url,
    name: dealer.displayName,
    telephone: dealer.phoneRegional,
    aggregateRating: {
      '@type': 'http://schema.org/AggregateRating',
      ratingValue: dealer.averageOverallStars,
      reviewCount: reviews.length,
    },
    address: {
      '@type': 'http://schema.org/PostalAddress',
      streetAddress: street,
      addressLocality: city,
      addressRegion: state,
      postalCode: zip,
    },
  };
}

function DealerPage({ dealerPage, errorStatusCode, indexable }: Props): JSX.Element {
  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Research });

  useContentGroups({ default: DEALER_PAGE_URL });

  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Dealers,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  if (!dealerPage || errorStatusCode) {
    return <Error statusCode={errorStatusCode} />;
  }

  const { titleTag, metadescription, url, enabled } = dealerPage;

  return (
    <TrackingDataContext.Provider
      value={{ source_page: SourcePages.DealerPage, dealerPageUrl: url }}
    >
      <>
        <Layout title={titleTag} description={metadescription || ''} indexed={enabled && indexable}>
          <Dealer dealerPage={dealerPage} />
        </Layout>
        <StructuredDataComponent data={generateStructuredData(dealerPage)} />
      </>
    </TrackingDataContext.Provider>
  );
}

export async function getServerSideProps({
  query,
  req,
  res,
}: GetServerSidePropsContext): Promise<{ props: Props }> {
  const indexable = isIndexable(req);
  let dealerPage;

  try {
    const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug || '';
    dealerPage = await fetchDealerPage(slug);
  } catch (error) {
    captureException(error);
    // When there is an api error, we don't want the page to 500 because we want
    // graceful error handling on Autolist-web, the api has its own error monitoring
    const errorStatusCode = error.status || 500;
    if (res) res.statusCode = errorStatusCode;
    return { props: { errorStatusCode, indexable } };
  }

  enableContentCaching(res);

  return {
    props: {
      dealerPage,
      indexable,
    },
  };
}

export default DealerPage;
